/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { booleanAttribute, Component, Input } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';

export enum VorgangStatusLabel {
  NEU = 'Neu',
  ANGENOMMEN = 'Angenommen',
  VERWORFEN = 'Verworfen',
  IN_BEARBEITUNG = 'In Bearbeitung',
  BESCHIEDEN = 'Beschieden',
  ZURUECKGESTELLT = 'Zurückgestellt',
  ABGESCHLOSSEN = 'Abgeschlossen',
  WEITERGELEITET = 'Weitergeleitet',
  ZU_LOESCHEN = 'Zu Löschen',
}

export const dotVariants = cva('size-3.5 rounded-full border', {
  variants: {
    variant: {
      blue: 'bg-status-blue/40 border-status-blue',
      green: 'bg-status-green/40 border-status-green',
      yellow: 'bg-status-yellow/40 border-status-yellow',
      gray: 'bg-status-gray/40 border-status-gray',
      white: 'bg-whitetext border-status-gray',
    },
    selected: {
      false: null,
    },
  },
  defaultVariants: {
    variant: 'blue',
    selected: false,
  },
  compoundVariants: [
    {
      variant: 'blue',
      selected: true,
      class: '[&]:bg-status-blue',
    },
    {
      variant: 'green',
      selected: true,
      class: '[&]:bg-status-green',
    },
    {
      variant: 'yellow',
      selected: true,
      class: '[&]:bg-status-yellow',
    },
    {
      variant: ['gray', 'white'],
      selected: true,
      class: '[&]:bg-status-gray',
    },
  ],
});
export type DotVariants = VariantProps<typeof dotVariants>;

@Component({
  selector: 'ods-vorgang-status',
  template: `<div [class]="dotVariants({ variant: statusVariant, selected })"></div>
    <p><span class="sr-only">Status: </span>{{ status }}</p>`,
  host: { class: 'flex items-center text-text gap-3' },
})
export class VorgangStatusComponent {
  @Input() status: VorgangStatusLabel;
  @Input({ transform: booleanAttribute }) selected: boolean = false;

  get statusVariant(): DotVariants['variant'] {
    switch (this.status) {
      case VorgangStatusLabel.NEU:
        return 'blue';
      case VorgangStatusLabel.ANGENOMMEN:
        return 'yellow';
      case VorgangStatusLabel.VERWORFEN:
        return 'gray';
      case VorgangStatusLabel.IN_BEARBEITUNG:
        return 'yellow';
      case VorgangStatusLabel.BESCHIEDEN:
        return 'green';
      case VorgangStatusLabel.ZURUECKGESTELLT:
        return 'blue';
      case VorgangStatusLabel.ABGESCHLOSSEN:
        return 'green';
      case VorgangStatusLabel.WEITERGELEITET:
        return 'green';
      case VorgangStatusLabel.ZU_LOESCHEN:
        return 'white';
      default:
        return 'blue';
    }
  }

  public readonly dotVariants = dotVariants;
}
